import React, { useMemo, useState } from 'react';
import { useFitStore } from '../store/useFitStore';
import type { Lead, LeadStatus } from '../store/types';
import LeadEditor, { LEAD_STATUSES } from '../components/coach/LeadEditor';

/* ---------- date helpers (local, no UTC drift) ---------- */
const isoLocal = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtDate = (iso?: string): string =>
  iso ? new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—';

const STATUS_LABEL: Record<LeadStatus, string> = Object.fromEntries(
  LEAD_STATUSES.map((s) => [s.key, s.label])
) as Record<LeadStatus, string>;

/* follow-up urgency for an active lead */
type Due = 'overdue' | 'today' | 'upcoming' | 'none';
const dueState = (lead: Lead, today: string): Due => {
  if (lead.status === 'won' || lead.status === 'lost' || !lead.followUpAt) return 'none';
  if (lead.followUpAt < today) return 'overdue';
  if (lead.followUpAt === today) return 'today';
  return 'upcoming';
};

const StatusChip: React.FC<{ s: LeadStatus }> = ({ s }) => (
  <span
    className="text-[9px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded leading-none whitespace-nowrap"
    style={{
      border: '1px solid rgba(255,255,255,0.15)',
      color: s === 'won' ? '#0a0a0a' : s === 'lost' ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.8)',
      background: s === 'won' ? 'rgba(255,255,255,0.9)' : 'transparent',
    }}
  >
    {STATUS_LABEL[s]}
  </span>
);

const DueTag: React.FC<{ d: Due; date?: string }> = ({ d, date }) => {
  if (d === 'overdue' || d === 'today')
    return (
      <span className="text-[9px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded leading-none border border-fit-line-strong text-white whitespace-nowrap">
        {d === 'today' ? 'Due today' : 'Overdue'}
      </span>
    );
  if (d === 'upcoming') return <span className="text-[11px] text-fit-text-secondary whitespace-nowrap">follow up {fmtDate(date)}</span>;
  return null;
};

const Leads: React.FC = () => {
  const { user, leads } = useFitStore();
  const [view, setView] = useState<'list' | 'board'>('list');
  const [editing, setEditing] = useState<{ lead: Lead; isNew: boolean } | null>(null);

  const today = isoLocal(new Date());
  const dueCount = useMemo(
    () => leads.filter((l) => { const d = dueState(l, today); return d === 'overdue' || d === 'today'; }).length,
    [leads, today]
  );

  const newLead = () =>
    setEditing({ isNew: true, lead: { id: crypto.randomUUID(), coachId: user?.id || '', name: '', status: 'new', createdAt: today } });

  /* ---------- list: active leads first, sorted by follow-up urgency ---------- */
  const rank = (l: Lead): number => {
    if (l.status === 'won') return 5;
    if (l.status === 'lost') return 6;
    const d = dueState(l, today);
    return d === 'overdue' ? 0 : d === 'today' ? 1 : d === 'upcoming' ? 2 : 3;
  };
  const sorted = useMemo(
    () =>
      [...leads].sort((a, b) => {
        const r = rank(a) - rank(b);
        if (r !== 0) return r;
        return (a.followUpAt || '9999').localeCompare(b.followUpAt || '9999');
      }),
    [leads, today] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="w-full max-w-[1240px] mx-auto px-4 md:px-8 py-6">
      {/* header */}
      <div data-tour="leads" className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="mono-label">Pipeline</p>
          <h1 className="text-[26px] font-bold tracking-tight leading-tight">
            Leads
            {dueCount > 0 && (
              <span className="ml-3 text-[12px] font-semibold uppercase tracking-[0.06em] text-fit-text-secondary align-middle">
                {dueCount} follow-up{dueCount === 1 ? '' : 's'} due
              </span>
            )}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-fit-bg-tertiary rounded-full p-1">
            {(['list', 'board'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${
                  view === v ? 'bg-white text-fit-ink' : 'text-fit-text-secondary hover:text-white'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <button onClick={newLead} className="btn btn-primary btn-sm">+ New lead</button>
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="text-center py-20 border border-fit-line rounded-fit-lg">
          <p className="text-[15px] font-semibold mb-1">No leads yet</p>
          <p className="text-[13px] text-fit-muted mb-4 max-w-[320px] mx-auto">Track consultations and prospects here so follow-ups don't slip.</p>
          <button onClick={newLead} className="btn btn-glass btn-sm">+ Add your first lead</button>
        </div>
      ) : view === 'list' ? (
        /* ---------------- LIST ---------------- */
        <div className="flex flex-col gap-1.5">
          {sorted.map((l) => {
            const d = dueState(l, today);
            const dim = l.status === 'won' || l.status === 'lost';
            return (
              <button
                key={l.id}
                onClick={() => setEditing({ lead: l, isNew: false })}
                className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-fit-md border transition-colors ${
                  d === 'overdue' || d === 'today' ? 'border-fit-line-strong bg-white/[0.03]' : 'border-fit-line hover:bg-white/[0.02]'
                } ${dim ? 'opacity-55' : ''}`}
              >
                <span className="w-9 h-9 rounded-full bg-fit-bg-tertiary flex items-center justify-center text-[11px] font-bold shrink-0">
                  {l.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-semibold truncate">{l.name}</span>
                    <StatusChip s={l.status} />
                  </div>
                  <p className="text-[11px] text-fit-muted truncate">
                    {[l.source, l.phone || l.email].filter(Boolean).join(' · ') || 'No contact info'}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <DueTag d={d} date={l.followUpAt} />
                  {l.status === 'consult_booked' && l.consultAt && (
                    <p className="text-[11px] text-fit-text-secondary">consult {fmtDate(l.consultAt)}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        /* ---------------- BOARD (drag between stages) ---------------- */
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
          {LEAD_STATUSES.map((col) => {
            const colLeads = sorted.filter((l) => l.status === col.key);
            return (
              <div
                key={col.key}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  const id = e.dataTransfer.getData('text/plain');
                  const l = leads.find((x) => x.id === id);
                  if (l && l.status !== col.key) useFitStore.getState().upsertLead({ ...l, status: col.key });
                }}
                className="shrink-0 w-[210px] rounded-fit-md border border-fit-line p-2 flex flex-col gap-2"
                style={{ minHeight: 200 }}
              >
                <div className="flex items-center justify-between px-1 pt-1">
                  <span className="mono-label">{col.label}</span>
                  <span className="text-[11px] text-fit-muted tabular-nums">{colLeads.length}</span>
                </div>
                {colLeads.map((l) => {
                  const d = dueState(l, today);
                  return (
                    <button
                      key={l.id}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData('text/plain', l.id)}
                      onClick={() => setEditing({ lead: l, isNew: false })}
                      className="text-left rounded-md bg-fit-bg-tertiary hover:bg-fit-bg-elevated border border-fit-line px-2.5 py-2 transition-colors cursor-grab active:cursor-grabbing"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[13px] font-semibold truncate">{l.name}</span>
                        {(d === 'overdue' || d === 'today') && (
                          <span className="w-1.5 h-1.5 rounded-full bg-white shrink-0" title="Follow-up due" />
                        )}
                      </div>
                      {l.source && <p className="text-[10px] text-fit-muted truncate mt-0.5">{l.source}</p>}
                      {d === 'upcoming' && <p className="text-[10px] text-fit-text-secondary mt-0.5">follow up {fmtDate(l.followUpAt)}</p>}
                      {col.key === 'consult_booked' && l.consultAt && <p className="text-[10px] text-fit-text-secondary mt-0.5">consult {fmtDate(l.consultAt)}</p>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {editing && <LeadEditor lead={editing.lead} isNew={editing.isNew} onClose={() => setEditing(null)} />}
    </div>
  );
};

export default Leads;
